import { useRef, type ReactNode } from 'react';
import { Check, Clock, FileWarning, Upload, X } from 'lucide-react';
import type { DocumentType } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { useUploadDocument, type UploadTarget } from '@/api/hooks/uploads';
import type { DocSlot, DocSlotState, OrderDocSet } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { toast } from '@/stores/toast';

/**
 * ТРИТЕ ДОКУМЕНТА НА ЗАЯВКА — правилото на клиента: всяка заявка в курса
 * събира заявка, ЧМР и фактура. Зелено — приет в офиса; жълто — качен, чака
 * преглед; червено — върнат; сиво — липсва.
 */
export const DOC_SLOTS = [
  { key: 'transportOrder', label: 'Заявка', type: 'transport_order' },
  { key: 'cmr', label: 'ЧМР', type: 'cmr' },
  { key: 'invoice', label: 'Фактура', type: 'invoice' },
] as const satisfies ReadonlyArray<{ key: keyof Omit<OrderDocSet, 'accepted' | 'complete'>; label: string; type: DocumentType }>;

const STATE: Record<DocSlotState, { tone: string; icon: typeof Check; text: string }> = {
  accepted: { tone: 'border-ok/40 bg-ok-soft text-ok', icon: Check, text: 'приет' },
  pending: { tone: 'border-warn/40 bg-warn-soft text-warn', icon: Clock, text: 'за преглед' },
  rejected: { tone: 'border-danger/40 bg-danger-soft text-danger', icon: X, text: 'върнат' },
  missing: { tone: 'border-line-strong bg-raised text-ink-faint', icon: FileWarning, text: 'липсва' },
};

/** Три малки отметки в ред — за списъци и за реда на заявка в курса. */
export function DocSetChips({ docs, className }: { docs: OrderDocSet; className?: string }) {
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {DOC_SLOTS.map((slot) => {
        const value = docs[slot.key];
        const meta = STATE[value.state];
        const Icon = meta.icon;
        return (
          <span
            key={slot.key}
            title={`${slot.label}: ${meta.text}${value.reference ? ` (${value.reference})` : ''}`}
            className={cn('inline-flex items-center gap-0.5 rounded border px-1 py-px text-[10px] font-medium', meta.tone)}
          >
            <Icon className="size-2.5" />
            {slot.label}
          </span>
        );
      })}
    </span>
  );
}

/** Отметка с действие: какво има и бутон „Качи", когато липсва или е върнат. */
export function DocSlotRow({
  label,
  slot,
  type,
  target,
  extra,
}: {
  label: string;
  slot: DocSlot;
  type: DocumentType;
  target: Omit<UploadTarget, 'type'>;
  extra?: ReactNode;
}) {
  const meta = STATE[slot.state];
  const Icon = meta.icon;
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5">
      <span className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium', meta.tone)}>
        <Icon className="size-3" />
        {label}
      </span>
      <span className="text-xs text-ink-dim">
        {meta.text}
        {slot.source === 'ecmr' ? ' · електронно ЧМР' : ''}
        {slot.source === 'issued_invoice' ? ` · издадена ${slot.reference ?? ''}` : ''}
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        {extra}
        {slot.state !== 'accepted' ? <UploadButton type={type} target={target} label={slot.state === 'missing' ? 'Качи' : 'Качи нов'} /> : null}
      </span>
    </div>
  );
}

/** Бутон, който отваря избор на файл и го качва към заявката/курса/буса/шофьора. */
export function UploadButton({
  type,
  target,
  label = 'Качи',
  accept = 'application/pdf,image/*',
  size = 'sm',
}: {
  type: DocumentType;
  target: Omit<UploadTarget, 'type'>;
  label?: string;
  accept?: string;
  size?: 'sm' | 'md';
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadDocument();
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          upload.mutate(
            { file, target: { ...target, type } },
            { onSuccess: () => toast.ok('Файлът е качен', file.name) },
          );
        }}
      />
      <Button
        size={size}
        variant="secondary"
        icon={<Upload className="size-3.5" />}
        loading={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {label}
      </Button>
    </>
  );
}
